/* eslint-disable no-plusplus */
/* eslint-disable max-lines */
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { orderSource } from 'enums/order.enum';
import sha256 from 'js-sha256';
import {
  capitalize,
  find,
  flatten,
  isEqual,
  isPlainObject,
  keyBy,
  keys,
  map,
  omitBy,
  replace,
  some,
  sortBy,
  toLower,
  transform,
} from 'lodash';
import { useSelector } from 'react-redux';
import { getTracking } from 'ts-tracking-number';
// eslint-disable-next-line import/no-unresolved
import { download, generateCsv, mkConfig } from 'export-to-csv';
// eslint-disable-next-line import/no-unresolved
import { jsPDF } from 'jspdf';
import * as pdfjsLib from 'pdfjs-dist';

import WorkQueues from 'enums/workQueue.enum';
import orderFillType from './constants/orderFillType';
import { PrescriptionFormattedStatus } from './constants/prescriptionStatus';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf5.worker.mjs';

export const EnableShippingService =
  process.env.REACT_APP_ENABLE_SHIPPING_SERVICE === 'true' || false;

export const EnableInventory = process.env.REACT_APP_ENABLE_INVENTORY === 'true' || false;

// POM-5645: inventory sub-switches. Dispense flow = single lot association + bulk physical-count
// lock + banner. Inventory lookup = catalog association / drug-profile-review lookup.
export const EnableDispenseFlow = process.env.REACT_APP_ENABLE_DISPENSE_FLOW === 'true' || false;

export const EnableInventoryLookup =
  process.env.REACT_APP_ENABLE_INVENTORY_LOOKUP === 'true' || false;

// POM-6083: manual lot entry. Defined in ./utils/featureFlags and re-exported here, because
// this module pulls in pdfjs-dist, jspdf and export-to-csv and a component that wants nothing
// but a boolean must not have to import all of that - see the note in that file.
export {
  AddManualLotEnabled,
  BlockUnusableLotItems,
  DisableInvoiceGroup,
  ShowDefaultPartnerContact,
} from './utils/featureFlags';

export { default as formatShipByDate } from './utils/formatShipByDate';

/* eslint-disable import/prefer-default-export */
export const normalizePhoneNumber = (value) => {
  if (!value) return value;
  const onlyNums = value.replace(/[^\d]/g, '');

  if (onlyNums.length <= 3) return onlyNums;
  if (onlyNums.length <= 7) return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3, 7)}`;

  return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3, 6)}-${onlyNums.slice(6, 10)}`;
};

export const hex2rgba = (hex, alpha = 1) => {
  const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

export const copyTextToClipBoard = (text) => navigator.clipboard.writeText(text);

export const encodeB64 = (str) => {
  return window.btoa(encodeURIComponent(str));
};

export const decodeB64 = (str) => {
  return decodeURIComponent(window.atob(str));
};

export const formatName = (str) => {
  const wordSplitRegex =
    /[a-zA-Z0-9àáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžæÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð'-]+/gu;
  return str
    ?.match(wordSplitRegex)
    .map((word) => word.replace(/(^|['-])(\p{L})/gu, (_, p1, p2) => p1 + p2.toUpperCase()))
    .join(' ');
};

export const truncateString = (str, num) => {
  if (str.length <= num) {
    return str;
  }
  return `${str.slice(0, num)}...`;
};

export const unMatchedFields = (orgData, modifiedData) =>
  keys(omitBy(orgData, (value, key) => isEqual(value, modifiedData[key])));

const toLowerCaseDeep = (obj) =>
  transform(
    obj,
    (accumulator, value, key) => {
      if (isPlainObject(value)) {
        accumulator[key] = toLowerCaseDeep(value);
      } else if (typeof value === 'string') {
        accumulator[key] = value.toLowerCase();
      } else {
        accumulator[key] = value;
      }
    },
    {}
  );

export const isDuplicatesFound = (collection, dataToValidate) =>
  some(collection, (item) => isEqual(toLowerCaseDeep(item), toLowerCaseDeep(dataToValidate)));

export const formatZipCode = (value) => {
  if (!value) return '';

  return replace(value, /[^0-9]/g, '')
    .match(/.{1,5}/g)
    ?.join('-');
};

export const useGetPartnerAccountName = () => {
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);

  return keyBy(flatten(map(partners, 'partnerAccount')), 'partnerAccountId');
};

export const transformZipCode = (zipCode) => {
  if (zipCode?.length === 9) {
    return `${zipCode.slice(0, 5)}-${zipCode.slice(5)}`;
  }

  return zipCode;
};

export const casingFormat = (input) => {
  return input?.trim().split(/\s+/).map(capitalize).join(' ');
};

export const transformAndGenerateHash = (text) => {
  const transformedText = text.replace(/\s+/g, '').toLowerCase();
  const hashedText = sha256(transformedText);

  return hashedText;
};

export const formatStateFilterLabel = (filterValues, name, maxVisible = 1) => {
  if (!filterValues || filterValues.length === 0) return `${name} = (none)`;

  const visible = filterValues.slice(0, maxVisible);
  const hiddenCount = filterValues.length - maxVisible;

  return (
    <>
      {`${name} = ${visible.join(', ')}`}
      {hiddenCount > 0 && <strong>{` +${hiddenCount} more`}</strong>}
    </>
  );
};

export const extractZipCode = (input) => {
  const match = input.match(/^\d{5}/);

  if (match) {
    return match[0];
  }

  return null;
};

export const extractNullLineItemsKey = (shipmentItems) => {
  const lineItemKeys = shipmentItems
    ?.map((item) => item?.lineItemKey)
    .filter((key) => key != null && typeof key === 'string' && key.trim() !== '');
  return lineItemKeys;
};

export const SELF_PRESCRIBE_WARNING = (rxNumber) => {
  const rxPart = rxNumber ? ` for Rx ${rxNumber}` : '';
  return `Patient and Prescriber may be the same person${rxPart}. Please review for potential self-prescribing.`;
};

// Intentional name-only heuristic: matches first+last name across both formats.
// NPI is available via originalPrescriber.npi but is not used here — two unrelated
// people with the same name will produce a false-positive advisory warning.
// Impact is low (soft warning only), but consider adding NPI comparison if noise becomes a problem.
export const isSelfPrescribeMatchByParts = (
  patientName,
  prescriberFirstName,
  prescriberLastName
) => {
  if (!patientName || !prescriberFirstName || !prescriberLastName) return false;
  const idx = patientName.indexOf(', ');
  if (idx === -1) return false;
  const patientLastName = patientName.slice(0, idx).trim().toLowerCase();
  const patientFirstName = patientName
    .slice(idx + 2)
    .trim()
    .toLowerCase();
  return (
    patientFirstName === prescriberFirstName.trim().toLowerCase() &&
    patientLastName === prescriberLastName.trim().toLowerCase()
  );
};

const PRESCRIBER_TITLE_PREFIXES = new Set(['dr', 'mr', 'mrs', 'ms', 'miss', 'prof', 'rev']);
const PRESCRIBER_SUFFIX_TOKENS = new Set([
  'md',
  'do',
  'phd',
  'dds',
  'dmd',
  'np',
  'pa',
  'rn',
  'jr',
  'sr',
  'ii',
  'iii',
  'iv',
]);

// Intentional name-only heuristic: re-tokenizes a joined prescriberName string.
// Strips known title prefixes (Dr., Mr.) and credential/suffix tokens (MD, Jr) before matching.
// Compound last names ("Van Der Berg") still only compare the final token — use
// isSelfPrescribeMatchByParts when discrete firstName/lastName fields are available.
export const isSelfPrescribeMatch = (patientName, prescriberName) => {
  if (!patientName || !prescriberName) return false;
  // patientName format: "LastName, FirstName" — split on first ', ' only to handle
  // compound last names like "Smith, Jr." that contain additional commas.
  const idx = patientName.indexOf(', ');
  if (idx === -1) return false;
  const patientLastName = patientName.slice(0, idx).trim().toLowerCase();
  const patientFirstName = patientName
    .slice(idx + 2)
    .trim()
    .toLowerCase();
  let parts = prescriberName.trim().split(/\s+/);
  const firstNormalized = parts[0].replace(/\.$/, '').toLowerCase();
  if (PRESCRIBER_TITLE_PREFIXES.has(firstNormalized)) {
    parts = parts.slice(1);
  }
  while (parts.length > 1) {
    const last = parts[parts.length - 1].replace(/[.,]$/, '').toLowerCase();
    if (!PRESCRIBER_SUFFIX_TOKENS.has(last)) break;
    parts = parts.slice(0, -1);
  }
  if (parts.length < 2) return false;
  const prescriberFirstName = parts[0].toLowerCase();
  const prescriberLastName = parts[parts.length - 1].toLowerCase();
  return patientFirstName === prescriberFirstName && patientLastName === prescriberLastName;
};

export const hasValidOverride = (prescriptionData) => {
  const override = prescriptionData?.prescriptionOverride;
  const hasDrugName = Boolean(override?.drugProfile?.drugName?.trim());
  // productId flow (Inventory Lookup ON) carries no drug profile — the resolved catalog productId
  // stands in for the drug identity, so treat it as a valid override too.
  const hasProductId = Boolean(override?.productDetails?.productId?.trim());
  const hasSigText = Boolean(override?.sig?.sigText?.trim());

  return (hasDrugName || hasProductId) && hasSigText;
};

export const getTrackingInfo = ({ trackingNumber }) => {
  const trackingCourierNameMapping = {
    usps: 'USPS',
    fedex: 'FedEx',
    ups: 'UPS',
  };

  const trackingUrl = {
    usps: `https://tools.usps.com/go/TrackConfirmAction?tRef=fullpage&tLc=2&text28777=&tLabels=${trackingNumber}%2C&tABt=false`,
    ups: `https://www.ups.com/track?loc=en_US&tracknum=${trackingNumber}&requester=ST/trackdetails`,
    fedex: `https://www.fedex.com/wtrk/track/?trknbr=${trackingNumber}`,
  };

  const carrierCode = trackingNumber && getTracking(trackingNumber)?.courier?.code;

  return {
    trackingNumber,
    carrierName: trackingCourierNameMapping[carrierCode],
    trackingUrl: trackingUrl[carrierCode],
  };
};

export const formatEnumLabel = (value, defaultValue = '-') => {
  if (!value) return defaultValue;
  return casingFormat(value.replace(/_/g, ' '));
};

export const formatReviewReason = (reason) => {
  if (!reason) return '';
  return casingFormat(reason.replace(/_/g, ' '));
};

const bulkQueueHeaders = [
  { key: 'orderId', displayLabel: 'Order Id' },
  { key: 'orderDate', displayLabel: 'Order Date' },
  { key: 'itemCount', displayLabel: '# Items' },
  { key: 'patientName', displayLabel: 'Patient' },
  { key: 'state', displayLabel: 'State' },
  { key: 'medicationName', displayLabel: 'Drug Name' },
  { key: 'isOtc', displayLabel: 'Type' },
  { key: 'quantity', displayLabel: 'Qty' },
  { key: 'createdAt', displayLabel: 'Label Date' },
  { key: 'partner', displayLabel: 'Partner' },
  { key: 'practice', displayLabel: 'Practice' },
  { key: 'shippingOption', displayLabel: 'Shipping' },
  { key: 'source', displayLabel: 'Source' },
  { key: 'facility', displayLabel: 'Facility' },
  { key: 'shipByDate', displayLabel: 'Ship By Date' },
];

const prescriptionReviewHeaders = [
  { key: 'orderId', displayLabel: 'Order Id' },
  { key: 'status', displayLabel: 'Status' },
  { key: 'drugName', displayLabel: 'Drug Name' },
  { key: 'patientName', displayLabel: 'Patient' },
  { key: 'rxNumber', displayLabel: 'Rx Number' },
  { key: 'fillType', displayLabel: 'Fill Type' },
  { key: 'shippingOption', displayLabel: 'Shipping Option' },
  { key: 'requestedDate', displayLabel: 'Requested Date' },
  { key: 'shipByDate', displayLabel: 'Ship By Date' },
  { key: 'needsByDate', displayLabel: 'Needs By Date' },
  { key: 'source', displayLabel: 'Source' },
];

export const exportOrdersToCSV = (items, facilities, shippingOptions, preset) => {
  const columnHeadersHandler = {
    [WorkQueues.BULK_LABELS]: bulkQueueHeaders,
    [WorkQueues.PRESCRIPTION_REVIEW]: prescriptionReviewHeaders,
  };

  const csvConfig = mkConfig({
    filename: `WorkQueue-${preset}-export-${dayjs().format('YYYY-MM-DDTHH:mm:ssZ[Z]')}`,
    showTitle: false,
    title: '',
    columnHeaders: columnHeadersHandler[preset],
  });

  const data = map(items, (item) => {
    const fillingFacility = find(
      facilities,
      (facility) => facility.fulfillingPharmacyId === item.fulfillingPharmacyId
    );

    const facilityName = fillingFacility?.name;
    const facilityState = fillingFacility?.state;

    return {
      shipByDate: item.shipByDate ? format(new Date(item.shipByDate), 'MM/dd/yyyy') : 'NA',
      needsByDate: item.needsByDate ? format(new Date(item.needsByDate), 'MM/dd/yyyy') : 'NA',
      requestedDate: item.requestedDate ? format(new Date(item.requestedDate), 'MM/dd/yyyy') : 'NA',
      fillType: item.fillType ? orderFillType[item.fillType] : 'NA',
      rxNumber: item.rxNumber || 'NA',
      orderId: item?.orderId || 'NA',
      orderDate: item.orderDate ? format(new Date(item.orderDate), 'MM/dd/yyyy') : 'NA',
      itemCount: item?.orderLineItemCount || 'NA',
      patientName: item.patientName || item.patient.patientName || 'NA',
      state: item.state || 'NA',
      medicationName: item?.medicationName || 'NA',
      drugName: item.drugName || 'NA',
      isOtc: item.isOtc ? 'OTC' : 'RX',
      quantity: item?.quantity || 'NA',
      createdAt: item?.createdAt ? format(new Date(item.createdAt), 'MM/dd/yyyy') : 'NA',
      partner: item?.partner || 'NA',
      practice: item?.practice || 'NA',
      shippingOption: item?.shippingOption
        ? find(shippingOptions, (option) => option.optionCode === item.shippingOption)
            ?.shippingOption || item.shippingOption
        : 'NA',
      source: item.source ? orderSource[item.source].sourceKey || 'NA' : 'NA',
      facility: item.fulfillingPharmacyId ? `${facilityName} - ${facilityState}` : 'NA',
      status: item.status
        ? PrescriptionFormattedStatus[item.status]
        : capitalize(toLower(item.status)),
    };
  });

  const csv = generateCsv(csvConfig)(data);

  download(csvConfig)(csv);
};

export const convertPdfToZPLII = async (blobUrl, dpi = 203) => {
  // 1. Load PDF Page
  const loadingTask = pdfjsLib.getDocument(blobUrl);
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);

  // 2. Mathematically Perfect Scaling
  const scale = dpi / 72;
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);

  // 3. Render PDF to Canvas
  await page.render({ canvasContext: context, viewport }).promise;

  // 4. Convert to ZPL II Hex (GFA Format)
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;
  const rowBytes = Math.ceil(canvas.width / 8);
  const totalBytes = rowBytes * canvas.height;

  let hexData = '';
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < rowBytes; x++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const pixelX = x * 8 + bit;
        if (pixelX < canvas.width) {
          const i = (y * canvas.width + pixelX) * 4;
          // Standard Grayscale Luminance
          const brightness = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;

          // Threshold: if darker than 128, set bit to 1 (black)
          if (brightness < 128) {
            // eslint-disable-next-line no-bitwise
            byte |= 1 << (7 - bit);
          }
        }
      }
      hexData += byte.toString(16).padStart(2, '0').toUpperCase();
    }
    hexData += '\n'; // Keeps the ZPL scannable/readable
  }

  // 5. Final ZPL II Output
  // ^PW sets the print width, ^LL sets the label length
  return `^XA
^PW${canvas.width}
^LL${canvas.height}
^FO0,0^GFA,${totalBytes},${totalBytes},${rowBytes},
${hexData}
^FS
^XZ`;
};

export const convertZplBlobToPdfUrl = async (zplBlob, options = { width: 4, height: 8 }) => {
  let zplString = await zplBlob.text();

  const dotsHeight = Math.round(options.height * 300);
  const dotsWidth = Math.round(options.width * 300);

  if (zplString.includes('^LL')) {
    zplString = zplString.replace(/\^LL\d+/, `^LL${dotsHeight}`);
  } else {
    zplString = zplString.replace('^XA', `^XA^LL${dotsHeight}`);
  }

  if (zplString.includes('^PW')) {
    zplString = zplString.replace(/\^PW\d+/, `^PW${dotsWidth}`);
  } else {
    zplString = zplString.replace('^XA', `^XA^PW${dotsWidth}`);
  }

  const { ready } = await import('zpl-renderer-js');
  const { api } = await ready;

  const base64Image = await api.zplToBase64Async(zplString, { density: 12 });
  const imageData = `data:image/png;base64,${base64Image}`;

  // eslint-disable-next-line new-cap
  const doc = new jsPDF({
    orientation: options.width > options.height ? 'landscape' : 'portrait',
    unit: 'in',
    format: [options.width, options.height],
    compress: false,
  });

  // 4. Map the 300 DPI image 1:1 onto the 4x6 inch PDF
  doc.addImage(imageData, 'PNG', 0, 0, options.width, 0, undefined, 'NONE');

  return URL.createObjectURL(doc.output('blob'));
};

export const sortTags = (tags) => {
  if (!tags || !Array.isArray(tags) || tags.length === 0) {
    return [];
  }
  return sortBy(tags, (tag) => String(tag?.tagName ?? '').toLowerCase());
};
